import React from "react";
import { ChevronDown } from "lucide-react";
import { SUPER_DOG_SPREAD } from "../utils/season";
import { LOSS_POINTS, SPREAD_BANDS, UNDERDOG_WIN_BONUS, WIN_POINTS } from "../games/pickThree";

// "How it scores" for the signed-out Pick Three page: five lines a newcomer can
// take in at a glance, and the detail (the spread bands, the underdog bonus,
// Super Dog, a worked example) behind a native <details>, so it works without
// script and is announced as expandable.
//
// Every number comes from games/pickThree.js and utils/season.js, and the band
// table is tested against the real scorer, so this cannot drift from scoring.

const signed = (n) => (n > 0 ? `+${n}` : String(n));
const top = SPREAD_BANDS[SPREAD_BANDS.length - 1].points;

const ROWS = [
  { label: "Your team wins", value: signed(WIN_POINTS) },
  { label: "Your team loses", value: signed(LOSS_POINTS) },
  { label: "Beats the spread", value: `+1 to +${top}` },
  { label: "Misses the spread", value: `-1 to -${top}` },
  { label: "Your captain", value: "Points x2" },
];

const cell = { padding: "7px 0", fontSize: 14, color: "var(--color-text-strong)" };
const num = { ...cell, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const small = { fontSize: 13, lineHeight: 1.5, color: "var(--color-text-muted)", margin: "10px 0 0" };

export default function PickThreeScoring() {
  return (
    <section
      aria-labelledby="p3-scoring"
      style={{ backgroundColor: "var(--color-bg-card)", border: "1.5px solid var(--color-bg-subtle)", borderRadius: 14, padding: "12px 14px", marginBottom: 16 }}
    >
      <h2 id="p3-scoring" style={{ fontSize: 15, fontWeight: 800, color: "var(--color-text-strong)", margin: "0 0 4px" }}>How it scores</h2>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {ROWS.map((r, i) => (
            <tr key={r.label} style={{ borderTop: i ? "1px solid var(--color-border)" : "none" }}>
              <td style={cell}>{r.label}</td>
              <td style={num}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <details style={{ marginTop: 6 }}>
        <summary
          className="p3-summary"
          style={{
            listStyle: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 0 2px", fontSize: 13, fontWeight: 700, color: "var(--color-cta)",
          }}
        >
          How the points add up
          <ChevronDown size={16} aria-hidden="true" className="p3-chevron" />
        </summary>

        <p style={{ ...small, marginTop: 6 }}>
          The spread says how much a team is expected to win or lose by. Points depend on how far it beats or misses that number:
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ ...cell, fontSize: 12, textAlign: "left", color: "var(--color-text-faint)", fontWeight: 700 }}>By</th>
              <th style={{ ...num, fontSize: 12, color: "var(--color-text-faint)", fontWeight: 700 }}>Beats</th>
              <th style={{ ...num, fontSize: 12, color: "var(--color-text-faint)", fontWeight: 700 }}>Misses</th>
            </tr>
          </thead>
          <tbody>
            {SPREAD_BANDS.map((b) => (
              <tr key={b.label} style={{ borderTop: "1px solid var(--color-border)" }}>
                <td style={cell}>{b.label}</td>
                <td style={num}>{signed(b.points)}</td>
                <td style={num}>{signed(-b.points)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={small}>Landing exactly on the spread scores nothing for it.</p>
        <p style={small}>
          <b style={{ color: "var(--color-text-strong)" }}>Underdog win.</b> An underdog that wins outright gets {signed(UNDERDOG_WIN_BONUS)} more.
        </p>
        <p style={small}>
          <b style={{ color: "var(--color-text-strong)" }}>Super Dog.</b> An underdog by {SUPER_DOG_SPREAD} points or more loses nothing
          for losing. The spread points still count, so a heavy loss is still negative.
        </p>
        <p style={small}>
          <b style={{ color: "var(--color-text-strong)" }}>Example.</b> A team favoured by 7.5 wins by 21. That is a win ({signed(WIN_POINTS)})
          and it beats the spread by 13.5 (+2), so +7. As your captain, +14.
        </p>
      </details>
    </section>
  );
}
